<template>
  <v-menu :close-on-content-click="false" location="bottom start" @click.stop>
    <template #activator="{ props: menuProps }">
      <button v-if="size === 'md'" v-bind="menuProps" type="button" class="tag-picker__add" @click.stop>
        <v-icon size="14">mdi-plus</v-icon>
        <span>Add tag</span>
      </button>
      <v-btn
        v-else
        v-bind="menuProps"
        icon="mdi-plus"
        size="x-small"
        variant="text"
        density="compact"
        class="tag-picker__btn"
        @click.stop />
    </template>
    <v-card min-width="240" class="tag-picker__menu">
      <v-card-text class="pa-2">
        <div
          v-for="def in tagDefs"
          :key="def.key"
          class="tag-picker__item"
          :class="{ 'tag-picker__item--disabled': def.auto }"
          @click.stop="!def.auto && onToggle(def.key, !currentTags.includes(def.key))">
          <span class="tag-picker__dot" :style="{ background: TAG_COLORS[def.color] || TAG_COLORS.grey }" />
          <span class="tag-picker__label">{{ def.label }}</span>
          <span v-if="def.auto" class="tag-picker__auto">auto</span>
          <v-icon v-if="currentTags.includes(def.key)" size="16" color="accent" class="tag-picker__check">
            mdi-check
          </v-icon>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script setup>
import { computed } from "vue";
import { TAG_COLORS } from "@/constants/tagColors";

const props = defineProps({
  tags: { type: Array, default: () => [] },
  tagDefs: { type: Array, default: () => [] },
  size: { type: String, default: "sm", validator: (v) => ["sm", "md"].includes(v) },
});

const emit = defineEmits(["update:tags"]);

const currentTags = computed(() => props.tags || []);

function onToggle(key, checked) {
  const userTags = currentTags.value.filter((t) => {
    const def = props.tagDefs.find((d) => d.key === t);
    return def && !def.auto;
  });
  let updated;
  if (checked) {
    updated = [...userTags, key];
  } else {
    updated = userTags.filter((t) => t !== key);
  }
  emit("update:tags", updated);
}
</script>

<style lang="scss" scoped>
.tag-picker__btn {
  opacity: 0.5;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
}

.tag-picker__add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 12px 0 9px;
  border: 1px dashed rgba($lh_color_navy, 0.25);
  border-radius: 13px;
  background: transparent;
  color: $lh_color_blue_gray;
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color $lh_transition_fast, border-color $lh_transition_fast, background $lh_transition_fast;

  &:hover {
    color: $lh_color_blue;
    border-color: rgba($lh_color_blue, 0.45);
    background: rgba($lh_color_blue, 0.04);
  }
}

.tag-picker__menu {
  max-height: 320px;
  overflow-y: auto;
  border-radius: 12px !important;
}

.tag-picker__item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: $lh_font_family_dm_sans;
  transition: background $lh_transition_fast;

  &:hover {
    background: $lh_color_snowy_white;
  }

  &--disabled {
    cursor: default;
    opacity: 0.6;

    &:hover {
      background: transparent;
    }
  }
}

.tag-picker__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.tag-picker__label {
  font-size: 13.5px;
  font-weight: 500;
  color: $lh_color_navy;
  flex: 1 1 auto;
}

.tag-picker__auto {
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: $lh_color_blue_gray;
}

.tag-picker__check {
  flex: 0 0 auto;
}
</style>
